import React, { useState, useMemo } from 'react';
import L from 'leaflet';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';


const LineCoverage = ({ lines, toggleVisibility, toggleAllVisibility, zoomToFeature, handleLineColorChange }) => {
    const [unit, setUnit] = useState('ft'); 
    const [expandedGroups, setExpandedGroups] = useState({});

    const calculateLength = (coords) => {
        if (!coords || coords.length < 2) return 0;
        let totalMeters = 0;
        for (let i = 0; i < coords.length - 1; i++) {
            const p1 = L.latLng(coords[i][0], coords[i][1]);
            const p2 = L.latLng(coords[i+1][0], coords[i+1][1]);
            totalMeters += p1.distanceTo(p2);
        }
        return totalMeters;
    };

    const formatValue = (meters) => {
        switch (unit) {
            case 'ft': return Math.round(meters * 3.28084).toLocaleString();
            case 'km': return (meters / 1000).toFixed(3);
            case 'm':
            default:   return Math.round(meters).toLocaleString();
        }
    };

    const groupedLines = useMemo(() => {
        return lines.reduce((acc, l) => {
            if (!acc[l.name]) acc[l.name] = [];
            const meters = calculateLength(l.coords);
            acc[l.name].push({ ...l, trueMeters: meters });
            return acc;
        }, {});
    }, [lines]);

    const allVisible = lines.every(l => l.visible);

    const totalMeters = useMemo(() => {
        return lines.reduce((acc, l) => acc + calculateLength(l.coords), 0);
    }, [lines]);

    const cycleUnit = () => {
        const units = ['ft', 'm', 'km'];
        setUnit(prev => units[(units.indexOf(prev) + 1) % units.length]);
    };

    return (
        <div className="coverage-section line-coverage-section">
            <div className="coverage-header">
                <div className="coverage-header-left">
                    <i className="fas fa-grip-lines" /> 
                    <span>Line Coverage</span>
                </div>

                <div className="total-badge">
                    <i className="fas fa-list-ol" />
                    <span>{lines.length} Lines</span>
                </div>

                <div className="coverage-header-right">
                    <button className="section-unit-toggle" onClick={cycleUnit} title={`Switch Unit (Current: ${unit})`}>
                        <i className="fas fa-ruler" />
                        <span style={{ marginLeft: '5px', fontSize: '0.7rem', fontWeight: 'bold' }}>{unit.toUpperCase()}</span>
                    </button>
                    <button className={`section-eye-toggle ${allVisible ? 'active' : ''}`} onClick={() => toggleAllVisibility(!allVisible)}>
                        <i className={`fas ${allVisible ? 'fa-eye' : 'fa-eye-slash'}`} />
                    </button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="coverage-table">
                    <tbody>
                        {Object.keys(groupedLines).map(groupName => {
                            const group = groupedLines[groupName];
                            const groupVisible = group.every(l => l.visible);
                            const isExpanded = expandedGroups[groupName];
                            const groupMeters = group.reduce((sum, l) => sum + l.trueMeters, 0);

                            return (
                                <React.Fragment key={groupName}>
                                    <tr className="group-header-row">
                                        <td className="feature-name-cell">
                                            <div className="feature-left">
                                                <button
                                                    className={`collapse-btn ${isExpanded ? 'active' : ''}`}
                                                    onClick={() => setExpandedGroups(p => ({ ...p, [groupName]: !p[groupName] }))}
                                                >
                                                    {/* <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} /> */}
                                                    {isExpanded ? <ChevronDown size={15} /> :  <ChevronRight size={15} />}  
                                                </button>
                                                <div className="feature-color-box" style={{ background: group[0].color, height: '4px', width: '20px', position: 'relative' }}>
                                                    <input
                                                        type="color"
                                                        value={group[0].color}
                                                        onChange={(e) => handleLineColorChange(groupName, e.target.value)}
                                                        style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer' }}
                                                    />
                                                </div>
                                                <div className="feature-name">
                                                    {groupName} <span className="count-badge">({group.length})</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="feature-value-cell">
                                            <div className="feature-right">
                                                <div className="feature-value-container">
                                                    <span className="feature-value">{formatValue(groupMeters)}</span>
                                                    <span className="feature-unit">{unit}</span>
                                                </div>
                                                <button
                                                    className="eye-toggle-small"
                                                    onClick={() => zoomToFeature(group.flatMap(l => l.coords))}
                                                >
                                                    <i className="fas fa-crosshairs" />
                                                </button>
                                                {/* FIX: was `l.visible !== !groupVisible` (backwards), now correctly `l.visible !== targetVisible` */}
                                                <button
                                                    className={`eye-toggle-small ${groupVisible ? 'active' : ''}`}
                                                    onClick={() => {
                                                        const targetVisible = !groupVisible;
                                                        group.forEach(l => {
                                                            if (l.visible !== targetVisible) {
                                                                toggleVisibility(l.id, 'line');
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <i className={`fas ${groupVisible ? 'fa-eye' : 'fa-eye-slash'}`} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>

                                    {isExpanded && group.map((l, idx) => (
                                        <tr key={l.id} className="sub-feature-row">
                                            <td className="feature-name-cell" style={{ paddingLeft: '40px' }}>
                                                <div className="feature-name" style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    <i className="fas fa-reply fa-rotate-180" style={{ marginRight: '8px', opacity: 0.5 }} />
                                                    Line {idx + 1}
                                                </div>
                                            </td>
                                            <td className="feature-value-cell">
                                                <div className="feature-right">
                                                    <span className="feature-value" style={{ fontSize: '0.8rem' }}>{formatValue(l.trueMeters)}</span>
                                                    <button className="eye-toggle-small" onClick={() => zoomToFeature(l.coords, l.id)}>
                                                        <i className="fas fa-map-marker-alt" />
                                                    </button>
                                                    <button
                                                        className={`eye-toggle-small ${l.visible ? 'active' : ''}`}
                                                        onClick={() => toggleVisibility(l.id, 'line')}
                                                    >
                                                        <i className={`fas ${l.visible ? 'fa-eye' : 'fa-eye-slash'}`} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="total-area-row">
                <div className="total-area-content">
                    <div className="total-area-label"><i className="fas fa-ruler-combined" /> Total Length</div>
                    <div className="total-area-value">
                        <span className="total-value">{formatValue(totalMeters)}</span>
                        <span className="total-unit">{unit}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LineCoverage;